import React, { useState, useEffect } from 'react';

interface SwitchProps {
  id: string;
  name: string;
  label: string;
  defaultChecked?: boolean;
  disabled?: boolean;
  onChange?: (checked: boolean) => void;
  className?: string;
  labelClassName?: string;
}

const Switch: React.FC<SwitchProps> = ({
  id,
  name,
  label,
  defaultChecked = false,
  disabled = false,
  onChange,
  className,
  labelClassName
}) => {
  const [isChecked, setIsChecked] = useState(defaultChecked);
  
  // Mettre à jour l'état local si la prop defaultChecked change
  useEffect(() => {
    setIsChecked(defaultChecked);
  }, [defaultChecked]);
  
  const handleToggle = () => {
    if (disabled) return;
    
    const newValue = !isChecked;
    setIsChecked(newValue);
    
    // Créer un input caché pour maintenir la compatibilité avec les formulaires
    const hiddenInput = document.createElement('input');
    hiddenInput.type = 'checkbox';
    hiddenInput.name = name;
    hiddenInput.checked = newValue;
    
    if (onChange) {
      onChange(newValue);
    }
  };
  
  return (
    <div className={`flex items-center ${className || ''}`}>
      <input 
        type="checkbox" 
        id={id} 
        name={name} 
        checked={isChecked} 
        onChange={() => {}} // Nécessaire pour React mais géré par le bouton
        className="sr-only" // Caché visuellement mais toujours présent pour les formulaires
      />
      
      <button
        type="button"
        role="switch"
        aria-checked={isChecked}
        aria-labelledby={`${id}-label`}
        onClick={handleToggle}
        disabled={disabled}
        className={`relative inline-block w-10 h-6 rounded-full focus:outline-none hover:opacity-90 transition-opacity mr-2 ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <div 
          className={`absolute inset-0 rounded-full transition-colors duration-200 ease-in-out ${isChecked ? 'bg-primary dark:bg-primary' : 'bg-gray-200 dark:bg-gray-700'}`}
        ></div>
        <div
          className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transform transition-transform duration-200 ease-in-out ${isChecked ? 'translate-x-4' : 'translate-x-0'}`}
        ></div>
      </button>
      
      <label 
        id={`${id}-label`} 
        htmlFor={id} 
        className={`select-none cursor-pointer ${labelClassName || 'text-sm'}`}
        onClick={handleToggle}
      >
        {label}
      </label>
    </div>
  );
};

export default Switch;